import { Inject, Injectable, Optional } from '@angular/core';
import { Store } from '@ngxs/store';
import { CookieService } from '@x/common/browser';
import { ChannelState } from '@x/ecommerce-shop/app/channel/state';
import { CheckoutOrderState } from '@x/ecommerce-shop/app/checkout/state/checkout-order.state';
import { ShopOrderState } from '@x/ecommerce-shop/app/core/order/state/shop-order.state';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import {
  IShopCollection,
  IShopProductDetail,
  IShopProductItem,
  IShopProductListingQuery,
  IShopProductSearchQuery,
  IShopProductSearchResult,
  ShopProductSearchService,
  ShopProductService,
  ShopWishlistService,
  TShopProductDetailByIdQuery,
  TShopProductItemQuery,
  TShopWishListItem,
  TShopWishListItemsQuery,
} from '@x/ecommerce/shop-client';
import { Observable, Subject, combineLatest, distinctUntilChanged } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { DEFAULT_REGION_ID, REGION_COOKIE_KEY } from './default-region';

@Injectable({ providedIn: 'root' })
export class RegionContextService {
  private _currentRegionId: number | undefined | null = this.defaultRegionId;
  private stateChanges = new Subject<void>();

  regionIdChange$ = this.stateChanges.pipe(
    startWith(null),
    map(() => this.currentRegionId),
    distinctUntilChanged(),
    shareReplay(),
  );

  set currentRegionId(id: number | undefined | null) {
    if (!id) {
      this.clearCurrentRegion();
      return;
    }

    this.cookieService.set(REGION_COOKIE_KEY, String(id), { expires: 120 });
    console.log('[RegionContext] SET REGION', this.currentRegionId);

    if (this.currentRegionId === id) return;

    this._currentRegionId = id;
    this.stateChanges.next();
  }

  get currentRegionId() {
    return this._currentRegionId;
  }

  constructor(
    private store: Store,
    private shopProductService: ShopProductService,
    private shopProductSearchService: ShopProductSearchService,
    private shopWishlistService: ShopWishlistService,
    private cookieService: CookieService,
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
    @Inject(DEFAULT_REGION_ID) @Optional() private defaultRegionId?: number | null | undefined,
  ) {
    // set from cookie
    const regionIdFromCookie = cookieService.get(REGION_COOKIE_KEY);
    if (regionIdFromCookie) {
      const parsedId = Number(regionIdFromCookie);
      if (!isNaN(parsedId)) {
        this._currentRegionId = parsedId;
      }
    } else {
      // If no cookie, initialize with injected default if available
      this._currentRegionId = this.defaultRegionId;
    }

    //update when region changes on order
    this.getRegionIdFromState$().subscribe((regionId) => {
      this.currentRegionId = regionId;
    });
  }

  clearCurrentRegion() {
    if (this.shopConfig.features.availability.useDefaultRegionFromChannel) {
      this._currentRegionId = this.store.selectSnapshot(ChannelState.defaultRegionId);
      console.info(
        '[RegionContext] Using region from Channel Default Fallback:',
        this._currentRegionId,
      );
      this.stateChanges.next();
      return;
    }

    this._currentRegionId = null;
    this.cookieService.delete(REGION_COOKIE_KEY);
    this.stateChanges.next();
  }

  fetchProductListing(
    query: IShopProductListingQuery,
  ): Observable<IShopCollection<IShopProductItem>> {
    return this.shopProductService.fetchProductListing({
      ...query,
      ...(this.currentRegionId && { regionId: this.currentRegionId }),
    });
  }

  fetchProductSearch(query: IShopProductSearchQuery): Observable<IShopProductSearchResult> {
    return this.shopProductSearchService.fetchProductSearch({
      ...query,
      ...(this.currentRegionId && { regionId: this.currentRegionId }),
    });
  }

  fetchMyFavourites(
    query: TShopWishListItemsQuery,
  ): Observable<IShopCollection<TShopWishListItem>> {
    return this.shopWishlistService.getWishListItems({
      ...query,
      ...(this.currentRegionId && { regionId: this.currentRegionId }),
    });
  }

  fetchProductItem$(query: TShopProductItemQuery): Observable<IShopProductItem> {
    return this.shopProductService.fetchProductItem({
      ...query,
      ...(this.currentRegionId && { regionId: this.currentRegionId }),
    });
  }

  fetchProductDetail$(query: TShopProductDetailByIdQuery): Observable<IShopProductDetail> {
    return this.shopProductService.fetchProductDetail({
      ...query,
      ...(this.currentRegionId && { regionId: this.currentRegionId }),
    });
  }

  /**
   * Determines the region ID based on state priority:
   * 1. ShopOrderState.regionId
   * 2. CheckoutOrderState.regionId
   * 3. ChannelState.defaultRegionId (if shopConfig.features.useChannelDefaultRegionFallback is true)
   * Emits only when a valid numeric ID is found and it's different from the currentRegionId.
   */
  private getRegionIdFromState$(): Observable<number> {
    return combineLatest([
      this.store.select(ShopOrderState.regionId),
      this.store.select(CheckoutOrderState.regionId),
      this.store.select(ChannelState.defaultRegionId),
    ]).pipe(
      map(([shopRegionId, checkoutRegionId, channelDefaultRegionIdFromState]) => {
        // Priority 1 & 2: Order States
        const regionIdFromOrderState = shopRegionId ?? checkoutRegionId;
        if (regionIdFromOrderState) {
          console.info('[RegionContext] Using region from Order State:', regionIdFromOrderState);
          return regionIdFromOrderState;
        }

        const useChannelFallback =
          this.shopConfig.features.availability.useDefaultRegionFromChannel === true;
        if (useChannelFallback && channelDefaultRegionIdFromState) {
          console.info(
            '[RegionContext] Using region from Channel Default Fallback:',
            channelDefaultRegionIdFromState,
          );
          return channelDefaultRegionIdFromState;
        }

        console.info('[RegionContext] No region found in state or fallback.');
        return null;
      }),
      filter((regionId): regionId is number => regionId !== null && regionId !== undefined),
      distinctUntilChanged(),
      filter((regionId) => {
        const changed = regionId !== this.currentRegionId;
        if (!changed) {
          console.info(
            `[RegionContext] Skipping update, derived region (${regionId}) matches current (${this.currentRegionId})`,
          );
        }
        return changed;
      }),
      map((regionId) => {
        console.info(`[RegionContext] Determined new region ID to set: ${regionId}`);
        return regionId;
      }),
    );
  }
}
